<script setup lang="ts">
import SVG_GAME_INTRO from '~/assets/svg/game-intro.svg'

withDefaults(defineProps<{
  content: string
  gameName: string
  controlExplain: string
}>(), {
  content: '',
  gameName: '',
  controlExplain: '',
})

const { locale } = useI18n()

const discordLink = ref(import.meta.env.VITE_DISCORD_URL)
</script>

<template>
  <section class="w-full flex flex-wrap items-center">
    <template v-if="locale !== 'en_US'">
      <template v-if="content">
        <SVG_GAME_INTRO />
        <h2 class="mis-2 text-6 c-#fff c-op-90 font-normal font-[Bebas_Neue]">
          {{ $t('gameDetail.gameIntroTitle') }}
        </h2>
        <div class="richtext mt-4 w-full c-#fff c-op-50 text-3! lh-1.5em!" v-html="content" />
      </template>
    </template>
    <template v-else>
      <div class="flex flex-col">
        <div class="flex items-center">
          <SVG_GAME_INTRO />
          <span class="mis-2 text-6 c-#fff c-op-90 font-normal font-[Bebas_Neue]">
            {{ $t('gameDetail.gameIntroTitle') }}
          </span>
        </div>
        <h2 class="mt-4 text-6 c-#fff c-op-90 font-normal font-[Bebas_Neue]">
          Keyboard Controls
        </h2>
        <div class="richtext w-full c-#fff c-op-50 text-3! lh-1.5em!" v-html="controlExplain" />
        <section class="richtext mt-4">
          <h2>Play {{ gameName }} Online for Free</h2>
          <p>Experience the thrill of {{ gameName }} online for free on EasyFun.gg. Dive into the action without any downloads or installations. Enjoy seamless gameplay through our advanced cloud gaming streaming service. With EasyFun, you can stream the game directly from our website, allowing you to enjoy high-quality gaming without the need for a high-end device.</p>
          <br>
          <p>Cloud gaming allows you to play high-quality games without the need for powerful hardware. Eliminating storage concerns and device limitations, making it the perfect solution for playing games like {{ gameName }} without the hassle. Whether you're worried about {{ gameName }} consuming your mobile storage or running into performance issues on older devices, EasyFun.gg offers a hassle-free way to enjoy your favorite games online.</p>
          <div class="mt-4 w-full c-#fff c-op-50 text-3! lh-1.5em!" v-html="content" />
          <h2>Play {{ gameName }} Without Downloading</h2>
          <p>Play {{ gameName }} directly from your browser on EasyFun.gg without the need for any downloads. Enjoy high-quality graphics and fullscreen options while saving valuable storage space on your device. It's incredibly simple: just visit EasyFun.gg, find your game, and log in to your EasyFun account to start playing immediately, no installations or complex setups required!</p>
          <br>
          <p><strong>Even without an EasyFun account</strong>, you can still enjoy 30 minutes of playtime without any commitments. With just three easy steps, you can start playing {{ gameName }} online for free, making it convenient to jump into the game right away.</p>
          <h2>How to Experience {{ gameName }} Better Online</h2>
          <p>To get the most out of your {{ gameName }} experience online, follow these key instructions. Whether you're playing on a school Chromebook, PC, or mobile device, EasyFun.gg ensures you can enjoy it without the usual restrictions or technical hurdles.</p>
          <h3>How to Play on a School Chromebook</h3>
          <p>Playing {{ gameName }} on a school Chromebook is easy with EasyFun.gg. To bypass network restrictions, you can install a VPN or use a proxy browser. Alternatively, connecting your Chromebook to a mobile hotspot is a simple solution. Once connected, visit EasyFun.gg, log in, and start playing without any downloads or installations. Enjoy {{ gameName }} unblocked and free, even on restricted networks.</p>
          <br>
          <p>Simply enter EasyFun.gg in your web browser’s address bar and start enjoying {{ gameName }} online for free on any PC, Mac, iOS, or Windows device. Playing games online with EasyFun not only saves storage space and avoids lengthy installations but also offers a low-cost, unlimited gaming experience.</p>
          <h3>Why Choose EasyFun.gg Over Emulators to Play On PC?</h3>
          <p>Unlike emulators, which often require complex configurations and high system resources, EasyFun.gg allows you to play {{ gameName }} on any device, including school Chromebooks, without any restrictions or technical barriers. While traditional emulators like LDPlayer or LDPlayerX, another product of LDPlayer studios offer features like multi-instance and macro keys, however they also demand significant system resources even though they are not affected by server distance or your internet speed.</p>
          <br>
          <p>EasyFun.gg is also another product of LDPlayer Studio that leverages years of expertise with Android emulators to provide a more efficient, less resource-intensive online emulator solution, offering a smooth gaming experience with optimized key mappings and no concerns about overheating, processing power, or storage consumption.</p>
          <h3>How to Play {{ gameName }} Online on Mobile Devices</h3>
          <p>Enjoy {{ gameName }} online for free on your mobile devices, including iPads, with ease through EasyFun.gg. Whether you're on the move or relaxing at home, you can access the game directly from your browser without any downloads or installations. EasyFun.gg ensures a seamless gaming experience anytime, anywhere, allowing you to jump into {{ gameName }} quickly and easily.</p>
          <br>
          <h3>Experience {{ gameName }} Online with Users Worldwide</h3>
          <p>Join a global community of gamers and play {{ gameName }} online for free with users from around the world. EasyFun.gg eliminates regional restrictions, so there's no need for VPNs. While server distance may cause high ping in some regions, we are continuously working to improve our service.</p>
          <br>
          <p>For global games, as long as the game has multiple languages, the site will determine the language of the game based on the language of the browser.</p>
          <h2>Is It Safe to Play {{ gameName }} on EasyFun?</h2>
          <p>Playing {{ gameName }} online for free on EasyFun.gg is safe and secure. We prioritize your account privacy and offer multi-device login options to enhance your experience. Email login ensures a secure and personalized gaming session. If you encounter any issues, our Discord community is available to provide support.</p>
          <h2>Tips for Common Problems Encountered When Playing {{ gameName }} Online</h2>
          <p>In order to ensure the best gaming experience with {{ gameName }} on EasyFun.gg, here are some helpful tips for overcoming common issues.</p>
          <ul class="pl-3">
            <li>1. <strong>Guest Account Binding: </strong> We recommend avoiding binding your account to a guest account as it poses a risk of data leakage.</li>
            <li>2. <strong>Game Loading Issues: </strong> If the game fails to load, try playing in incognito mode to bypass browser cache issues. Using Google Chrome often resolves loading problems. If the game still doesn’t load, try refreshing the page several times.</li>
            <li>3. <strong>Lower the Lag: </strong> For lag issues, use the HD button on the timer bar to reduce the resolution.</li>
            <li>4. <strong>How to Play with Keyboard: </strong>The question mark button next to the HD button shows the keyboard controls, and the square icon allows you to play in full screen mode.</li>
            <li>
              5. <strong>Additional Support: </strong>If you encounter more problems, visit our <NuxtLink rel="nofollow" class="color-green-5" target="_blank" to="/faq.html">
                FAQ
              </NuxtLink> page or join our <NuxtLink rel="nofollow" class="color-green-5" target="_blank" :to="discordLink">
                Discord
              </NuxtLink> for immediate assistance.
            </li>
          </ul>
        </section>
      </div>
    </template>
  </section>
</template>

<style lang="scss" scoped>
.richtext {
  --at-apply: 'c-#fff c-op-50 text-3! lh-1.5em!';
  word-break: break-word;

  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5) {
    --at-apply: 'text-6! c-#fff! c-op-90! font-normal! font-[Bebas_Neue] mt-4! lh-1.25em';
    strong {
      --at-apply: 'text-6! font-normal! font-[Bebas_Neue]';
    }

    span {
      --at-apply: 'text-6! font-normal! font-[Bebas_Neue] mb-0!';
    }
  }

  :deep(strong) {
    --at-apply: 'c-#fff! font-normal';
  }
}
</style>
